import { useEffect, useRef, useCallback } from 'react';
import type { InventoryItem, RoomPhoto } from '../types'; // Adjusted to import new RoomPhoto type
import { compressImage } from '../utils/imageUtils';
import { uploadPhotoToSupabase } from '../utils/supabaseStorage'; // Import uploader
import { v4 as uuidv4 } from 'uuid';

// Simple, robust UUID generator function
const generateUUID = (): string => {
  try {
    // Try to use crypto.randomUUID if available (modern browsers)
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    
    // Use imported uuid v4
    return uuidv4();
  } catch (error) {
    console.warn('[usePhotoHandling] UUID libraries not available, using fallback generator:', error);
    // Fallback UUID generator
    return 'photo-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  }
};

// Configurable timing constants for upload reliability
const UPLOAD_CONFIG = {
  BATCH_SIZE: 5, // Process 5 uploads at a time (increased from 2)
  UPLOAD_DELAY_BETWEEN_ITEMS: 200, // 200ms between items in a batch (reduced from 1000ms)
  UPLOAD_DELAY_BETWEEN_BATCHES: 500, // 500ms between batches (reduced from 2000ms)
  SUCCESS_STABILIZATION_DELAY: 100, // Wait after successful upload (reduced from 500ms)
  STATE_CONSISTENCY_DELAY: 100, // Wait for state updates to propagate (reduced from 200ms)
  RETRY_BASE_DELAY: 2000, // Base retry delay (2 seconds)
  RETRY_MAX_DELAY: 15000, // Maximum retry delay (15 seconds)
  COMPRESSION_TIMEOUT: 45000, // 45 seconds for image compression
  UPLOAD_TIMEOUT: 120000, // 120 seconds (2 minutes) for upload
};

// Interface for the special update instruction (might be simplified or removed)
// For now, let's assume updateRoomItem can take a RoomPhoto object directly
// or updates a specific photo in the array.

interface UsePhotoHandlingProps {
  updateRoomItem: (
    roomIndex: number,
    itemIndex: number,
    // updates: Partial<InventoryItem> | Partial<RoomPhoto> & { photoId: string } // Allow updating a specific photo metadata
    updates: Partial<InventoryItem> | { photoToUpdate: RoomPhoto } // Simpler: pass the whole photo object to update
  ) => void;
  getItemPhotos: (roomIndex: number, itemIndex: number) => RoomPhoto[];
  // Needed to construct filenames for Supabase and avoid conflicts if user navigates away or app closes mid-upload
  // It's better if these are available when a photo is added, not just when saving the whole inventory.
  getCurrentInventoryId: () => string | null; 
  // Callbacks for processing state management
  onProcessingStart?: (roomIndex: number, itemIndex: number) => void;
  onProcessingStop?: (roomIndex: number, itemIndex: number) => void;
  // Callbacks for batch upload optimization
  onBatchUploadStart?: () => void;
  onBatchUploadEnd?: () => void;
}

// Enhanced upload queue item with retry logic
interface UploadQueueItem {
  photo: RoomPhoto;
  roomIndex: number;
  itemIndex: number;
  retryCount: number;
  maxRetries: number;
}

export const usePhotoHandling = ({ 
  updateRoomItem, 
  getItemPhotos, 
  getCurrentInventoryId,
  onProcessingStart,
  onProcessingStop,
  onBatchUploadStart,
  onBatchUploadEnd
}: UsePhotoHandlingProps) => {

  // Enhanced refs for better concurrency control
  const ongoingUploadsRef = useRef<Set<string>>(new Set());
  const operationQueueRef = useRef<Map<string, Promise<void>>>(new Map());
  const uploadQueueRef = useRef<UploadQueueItem[]>([]);
  const isProcessingQueueRef = useRef<boolean>(false);
  const retryTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  
  // Add debug counter to track upload attempts
  const uploadAttemptCounterRef = useRef<number>(0);

  // Process upload queue with proper retry logic
  const processUploadQueue = useCallback(async () => {
    const queueLength = uploadQueueRef.current.length;
    const isProcessing = isProcessingQueueRef.current;
    
    console.log(`[DEBUG] processUploadQueue called - Queue length: ${queueLength}, Currently processing: ${isProcessing}`);
    
    if (isProcessingQueueRef.current || uploadQueueRef.current.length === 0) {
      console.log(`[DEBUG] processUploadQueue early return - Processing: ${isProcessing}, Queue empty: ${queueLength === 0}`);
      return;
    }

    isProcessingQueueRef.current = true;
    console.log(`[DEBUG] processUploadQueue starting batch processing - Queue length: ${queueLength}`);

    try {
      // Reduced batch size for more sequential processing and better reliability
      const batchSize = UPLOAD_CONFIG.BATCH_SIZE;
      const currentBatch = uploadQueueRef.current.splice(0, batchSize);
      
      console.log(`[DEBUG] Processing batch of ${currentBatch.length} items from queue`);
      console.log(`[DEBUG] Batch items:`, currentBatch.map(item => ({
        photoId: item.photo.id,
        roomIndex: item.roomIndex,
        itemIndex: item.itemIndex,
        retryCount: item.retryCount,
        hasLocalPath: !!item.photo.localPath
      })));

      // PHASE 2 OPTIMIZATION: Pre-compress all photos in batch in parallel
      console.log(`[DEBUG] Starting parallel compression for batch of ${currentBatch.length} photos`);
      const compressionStartTime = Date.now();
      
      const compressionPromises = currentBatch.map(async (queueItem) => {
        const { photo } = queueItem;
        try {
          if (!photo.localPath) {
            console.warn(`[DEBUG] No localPath for photo ${photo.id}, skipping compression`);
            return { queueItem, compressedDataUrl: null, error: 'No localPath' };
          }
          
          const compressionTimeout = new Promise<never>((_, reject) => {
            setTimeout(() => reject(new Error('Compression timeout')), UPLOAD_CONFIG.COMPRESSION_TIMEOUT);
          });
          
          const compressedDataUrl = await Promise.race([
            compressImage(photo.localPath, 1600, 0.9),
            compressionTimeout
          ]) as string;
          
          return { queueItem, compressedDataUrl, error: null };
        } catch (error) {
          console.error(`[DEBUG] Compression failed for photo ${photo.id}:`, error);
          return { queueItem, compressedDataUrl: null, error: (error as Error).message };
        }
      });
      
      const compressionResults = await Promise.allSettled(compressionPromises);
      const compressionTime = Date.now() - compressionStartTime;
      console.log(`[DEBUG] Parallel compression completed in ${compressionTime}ms for ${currentBatch.length} photos`);
      
      // Now upload with pre-compressed data
      const uploadPromises = compressionResults.map(async (result, index) => {
        if (result.status === 'rejected') {
          console.error(`[DEBUG] Compression promise rejected at index ${index}:`, result.reason);
          return;
        }
        
        const { queueItem, compressedDataUrl, error } = result.value;
        const { photo, roomIndex, itemIndex, retryCount, maxRetries } = queueItem;
        const uploadKey = `${roomIndex}-${itemIndex}-${photo.id}`;

        console.log(`[DEBUG] Starting upload for ${uploadKey} (batch index: ${index}, retry: ${retryCount}/${maxRetries})`);

        // Skip if compression failed
        if (error || !compressedDataUrl) {
          console.error(`[DEBUG] Skipping upload for ${uploadKey} due to compression error: ${error}`);
          if (retryCount < maxRetries) {
            // Retry the entire photo (including compression)
            const retryDelay = Math.min(
              UPLOAD_CONFIG.RETRY_BASE_DELAY * Math.pow(2, retryCount),
              UPLOAD_CONFIG.RETRY_MAX_DELAY
            );
            setTimeout(() => {
              uploadQueueRef.current.push({ ...queueItem, retryCount: retryCount + 1 });
              processUploadQueue();
            }, retryDelay);
          }
          return;
        }

        // Add staggered delay for batch items to prevent overwhelming the system
        if (index > 0) {
          const delay = UPLOAD_CONFIG.UPLOAD_DELAY_BETWEEN_ITEMS * index;
          console.log(`[DEBUG] Adding ${delay}ms delay for batch item ${index}`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }

        try {
          console.log(`[DEBUG] Calling uploadAndProcessPhoto for ${uploadKey} with pre-compressed data`);
          await uploadAndProcessPhoto(photo, roomIndex, itemIndex, compressedDataUrl);
          console.log(`[DEBUG] Successfully completed upload for ${uploadKey}`);
          
          // Add delay after successful upload to allow state to stabilize
          await new Promise(resolve => setTimeout(resolve, UPLOAD_CONFIG.SUCCESS_STABILIZATION_DELAY));
        } catch (error) {
          console.error(`[DEBUG] Upload failed for ${uploadKey}, retry ${retryCount}/${maxRetries}:`, error);
          
          if (retryCount < maxRetries) {
            // Increased retry delay with exponential backoff - more time for recovery
            const retryDelay = Math.min(
              UPLOAD_CONFIG.RETRY_BASE_DELAY * Math.pow(2, retryCount), 
              UPLOAD_CONFIG.RETRY_MAX_DELAY
            );
            const timeoutId = setTimeout(() => {
              console.log(`[DEBUG] Executing retry for ${uploadKey} after ${retryDelay}ms delay`);
              uploadQueueRef.current.push({
                ...queueItem,
                retryCount: retryCount + 1
              });
              processUploadQueue();
              retryTimeoutsRef.current.delete(uploadKey);
            }, retryDelay);
            
            retryTimeoutsRef.current.set(uploadKey, timeoutId);
            console.log(`[DEBUG] Scheduled retry for ${uploadKey} in ${retryDelay}ms`);
          } else {
            console.error(`[DEBUG] Final failure for ${uploadKey} after ${maxRetries} retries`);
            // Final failure - update photo with error
            updateRoomItem(roomIndex, itemIndex, {
              photoToUpdate: {
                ...photo,
                uploading: false,
                error: 'Upload failed after multiple retries'
              }
            });
          }
        }
      });

      console.log(`[DEBUG] Waiting for ${uploadPromises.length} upload promises to settle`);
      const results = await Promise.allSettled(uploadPromises);
      
      console.log(`[DEBUG] Batch results:`, results.map((result, index) => ({
        index,
        status: result.status,
        reason: result.status === 'rejected' ? result.reason?.message : 'success'
      })));

      // Increased delay before processing next batch to allow system recovery
      if (uploadQueueRef.current.length > 0) {
        console.log(`[DEBUG] Scheduling next batch processing in ${UPLOAD_CONFIG.UPLOAD_DELAY_BETWEEN_BATCHES}ms`);
        setTimeout(() => processUploadQueue(), UPLOAD_CONFIG.UPLOAD_DELAY_BETWEEN_BATCHES);
      } else {
        console.log(`[DEBUG] Upload queue is now empty - batch processing complete`);
        
        // Stop all processing indicators since uploads are complete
        const processedItems = new Set<string>();
        currentBatch.forEach(({ roomIndex, itemIndex }) => {
          const key = `${roomIndex}-${itemIndex}`;
          if (!processedItems.has(key)) {
            processedItems.add(key);
            onProcessingStop?.(roomIndex, itemIndex);
            console.log(`[DEBUG] Stopped processing indicator for R${roomIndex}I${itemIndex}`);
          }
        });
        
        // Resume autosave now that all uploads are complete
        if (onBatchUploadEnd) {
          console.log(`[Batch Upload] Resuming autosave after batch completion`);
          onBatchUploadEnd();
        }
      }
    } finally {
      isProcessingQueueRef.current = false;
      console.log(`[DEBUG] processUploadQueue finished - Processing flag reset`);
    }
  }, []);

  const uploadAndProcessPhoto = async (
    photoObjectToUpload: RoomPhoto,
    roomIndex: number,
    itemIndex: number,
    preCompressedDataUrl?: string // Optional pre-compressed data from parallel compression
  ) => {
    const uploadKey = `${roomIndex}-${itemIndex}-${photoObjectToUpload.id}`;
    
    console.log(`[DEBUG] uploadAndProcessPhoto started for ${uploadKey}`);
    console.log(`[DEBUG] Photo object:`, {
      id: photoObjectToUpload.id,
      fileName: photoObjectToUpload.fileName,
      hasLocalPath: !!photoObjectToUpload.localPath,
      localPathLength: photoObjectToUpload.localPath?.length,
      uploading: photoObjectToUpload.uploading,
      hasUrl: !!photoObjectToUpload.url
    });
    
    // Check if this photo is already being uploaded
    if (ongoingUploadsRef.current.has(uploadKey)) {
      console.warn(`[DEBUG] Upload already in progress for: ${uploadKey}`);
      return;
    }

    if (!photoObjectToUpload.localPath) {
      console.error(`[DEBUG] No localPath to upload for photo: ${photoObjectToUpload.id}`);
      updateRoomItem(roomIndex, itemIndex, { 
        photoToUpdate: { ...photoObjectToUpload, uploading: false, error: 'Missing local path for upload' } 
      });
      return;
    }

    // Mark upload as ongoing
    ongoingUploadsRef.current.add(uploadKey);
    console.log(`[DEBUG] Marked ${uploadKey} as ongoing. Ongoing uploads:`, Array.from(ongoingUploadsRef.current));

    try {
      // Ensure uploading status is set before starting
      console.log(`[DEBUG] Setting uploading status to true for ${uploadKey}`);
      updateRoomItem(roomIndex, itemIndex, { 
        photoToUpdate: { ...photoObjectToUpload, uploading: true, error: undefined } 
      });

      // Use pre-compressed data if provided, otherwise compress now
      let compressedDataUrl: string;
      
      if (preCompressedDataUrl) {
        console.log(`[DEBUG] Using pre-compressed data for ${uploadKey}`);
        compressedDataUrl = preCompressedDataUrl;
      } else {
        console.log(`[DEBUG] Starting image compression for ${uploadKey}`);
        const compressionTimeout = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Image compression timeout')), UPLOAD_CONFIG.COMPRESSION_TIMEOUT);
        });

        compressedDataUrl = await Promise.race([
          compressImage(photoObjectToUpload.localPath, 1600, 0.9),
          compressionTimeout
        ]) as string;
        
        console.log(`[DEBUG] Image compressed successfully for ${uploadKey}. Compressed size: ${compressedDataUrl.length} characters`);
      }
      
      // Use the fileName already generated when the photo was added
      const storagePath = photoObjectToUpload.fileName;
      console.log(`[DEBUG] Using storage path: ${storagePath}`);

      // Increased upload timeout for slower networks and larger files
      console.log(`[DEBUG] Starting Supabase upload for ${uploadKey}`);
      const uploadTimeout = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Upload timeout')), UPLOAD_CONFIG.UPLOAD_TIMEOUT);
      });

      const supabaseUrl = await Promise.race([
        uploadPhotoToSupabase(compressedDataUrl, storagePath, 'inventory-photos'),
        uploadTimeout
      ]) as string;
      
      console.log(`[DEBUG] Photo uploaded to Supabase successfully for ${uploadKey}:`, supabaseUrl);
      
      // Validate the URL before updating state
      if (!supabaseUrl || !supabaseUrl.startsWith('http')) {
        throw new Error(`Invalid URL returned from upload: ${supabaseUrl}`);
      }

      const updatedPhoto = {
        ...photoObjectToUpload,
        url: supabaseUrl,
        uploading: false,
        error: undefined,
        timestamp: new Date().toISOString(),
      };
      // Remove localPath completely instead of setting to undefined
      delete updatedPhoto.localPath;
      
      console.log(`[DEBUG] Preparing to update photo state for ${uploadKey}:`, {
        id: updatedPhoto.id,
        hasUrl: !!updatedPhoto.url,
        uploading: updatedPhoto.uploading,
        error: updatedPhoto.error
      });
      
      // Increased delay to ensure state consistency - give more time for state propagation
      await new Promise(resolve => setTimeout(resolve, UPLOAD_CONFIG.STATE_CONSISTENCY_DELAY));
      
      updateRoomItem(roomIndex, itemIndex, {
        photoToUpdate: updatedPhoto
      });
      
      console.log(`[DEBUG] Photo state updated successfully after upload for ${uploadKey}`);
    } catch (error) {
      console.error(`[DEBUG] Error uploading or processing photo for ${uploadKey}:`, error);
      console.error(`[DEBUG] Error details:`, {
        message: (error as Error).message,
        stack: (error as Error).stack
      });
      updateRoomItem(roomIndex, itemIndex, {
        photoToUpdate: {
          ...photoObjectToUpload,
          uploading: false,
          error: (error as Error).message || 'Upload failed',
        }
      });
      throw error; // Re-throw to trigger retry logic
    } finally {
      // Remove from ongoing uploads
      ongoingUploadsRef.current.delete(uploadKey);
      console.log(`[DEBUG] Removed ${uploadKey} from ongoing uploads. Remaining:`, Array.from(ongoingUploadsRef.current));
    }
  };

  // Enhanced photo addition with better synchronization
  const handlePhotoAddSynchronized = useCallback((
    dataUrls: string[],
    roomIndex: number,
    itemIndex: number
  ) => {
    const operationKey = `add-${roomIndex}-${itemIndex}-${Date.now()}`;
    
    console.log(`[DEBUG] handlePhotoAddSynchronized called with ${dataUrls.length} photos for R${roomIndex}I${itemIndex}`);
    console.log(`[DEBUG] Operation key: ${operationKey}`);
    
    // Queue the operation to prevent concurrent modifications
    const operation = operationQueueRef.current.get(`${roomIndex}-${itemIndex}`) || Promise.resolve();
    const newOperation = operation.then(() => 
      handlePhotoAddInternal(dataUrls, roomIndex, itemIndex)
    ).catch(error => {
      console.error(`[DEBUG] Error in photo add operation for ${operationKey}:`, error);
    }).finally(() => {
      // Clean up completed operation
      if (operationQueueRef.current.get(`${roomIndex}-${itemIndex}`) === newOperation) {
        operationQueueRef.current.delete(`${roomIndex}-${itemIndex}`);
        console.log(`[DEBUG] Cleaned up operation queue for R${roomIndex}I${itemIndex}`);
      }
    });
    
    operationQueueRef.current.set(`${roomIndex}-${itemIndex}`, newOperation);
    console.log(`[DEBUG] Queued operation for R${roomIndex}I${itemIndex}. Queue size:`, operationQueueRef.current.size);
    return newOperation;
  }, []);

  const handlePhotoAddInternal = async (
    dataUrls: string[],
    roomIndex: number,
    itemIndex: number
  ): Promise<void> => {
    try {
      console.log(`[DEBUG] handlePhotoAddInternal started with ${dataUrls.length} photos for R${roomIndex}I${itemIndex}`);
      
      // Suspend autosave for batch upload optimization
      if (dataUrls.length > 0 && onBatchUploadStart) {
        console.log(`[Batch Upload] Suspending autosave for ${dataUrls.length} photos`);
        onBatchUploadStart();
      }
      
      // Validate function dependencies
      if (typeof getCurrentInventoryId !== 'function') {
        throw new Error('getCurrentInventoryId is not a function');
      }
      
      const inventoryId = getCurrentInventoryId();
      console.log(`[DEBUG] Current inventory ID: ${inventoryId}`);
      
      if (!inventoryId) {
        console.error("[DEBUG] Cannot add photos: Missing currentInventoryId");
        return;
      }

      if (typeof getItemPhotos !== 'function') {
        throw new Error('getItemPhotos is not a function');
      }

      // Get current photos with additional safety checks
      let currentPhotos: RoomPhoto[] = [];
      try {
        currentPhotos = getItemPhotos(roomIndex, itemIndex) || [];
        console.log(`[DEBUG] Current photos count for R${roomIndex}I${itemIndex}: ${currentPhotos.length}`);
      } catch (error) {
        console.error(`[DEBUG] Error getting current photos for R${roomIndex}I${itemIndex}:`, error);
        currentPhotos = []; // Fallback to empty array
      }
      
      const newPhotoObjects: RoomPhoto[] = [];

      // Process each data URL with better error handling
      for (let index = 0; index < dataUrls.length; index++) {
        try {
          const dataUrl = dataUrls[index];
          console.log(`[DEBUG] Processing dataUrl ${index + 1}/${dataUrls.length} for R${roomIndex}I${itemIndex}`);
          
          // Validate data URL
          if (!dataUrl || !dataUrl.startsWith('data:image/')) {
            console.error(`[DEBUG] Invalid data URL at index ${index}:`, dataUrl?.substring(0, 50) + '...');
            continue;
          }
          
          const photoId = generateUUID();
          const newPhoto: RoomPhoto = {
            id: photoId,
            fileName: `${inventoryId}/${roomIndex}/${itemIndex}/${photoId}.jpg`,
            localPath: dataUrl,
            uploading: false, // Will be set to true when upload starts
            timestamp: new Date().toISOString(),
            order: currentPhotos.length + newPhotoObjects.length,
          };
          
          newPhotoObjects.push(newPhoto);
          console.log(`[DEBUG] Created photo object ${index + 1}/${dataUrls.length}:`, {
            id: newPhoto.id,
            fileName: newPhoto.fileName,
            order: newPhoto.order,
            dataUrlLength: dataUrl.length
          });
        } catch (error) {
          console.error(`[DEBUG] Error processing individual dataUrl at index ${index} for R${roomIndex}I${itemIndex}:`, error);
          // Continue with other photos instead of failing completely
        }
      }

      if (newPhotoObjects.length > 0) {
        console.log(`[DEBUG] Adding ${newPhotoObjects.length} new photos to R${roomIndex}I${itemIndex}`);
        
        if (typeof updateRoomItem !== 'function') {
          throw new Error('updateRoomItem is not a function');
        }
        
        // Update state with new photos
        const allPhotos = [...currentPhotos, ...newPhotoObjects];
        console.log(`[DEBUG] Total photos after addition: ${allPhotos.length} (${currentPhotos.length} existing + ${newPhotoObjects.length} new)`);
        
        updateRoomItem(roomIndex, itemIndex, { 
          photos: allPhotos 
        });
        
        // Do NOT stop processing here - wait until all uploads complete
        console.log(`[DEBUG] Photos added to state for R${roomIndex}I${itemIndex}, queuing uploads`);
        
        // Queue uploads with retry logic and deduplication
        newPhotoObjects.forEach((photoObj, index) => {
          const uploadKey = `${roomIndex}-${itemIndex}-${photoObj.id}`;
          
          // DEDUPLICATION: Check if photo is already queued or uploading
          const isAlreadyQueued = uploadQueueRef.current.some(
            item => item.photo.id === photoObj.id && 
                   item.roomIndex === roomIndex && 
                   item.itemIndex === itemIndex
          );
          const isCurrentlyUploading = ongoingUploadsRef.current.has(uploadKey);
          
          if (isAlreadyQueued || isCurrentlyUploading) {
            console.log(`[DEBUG] ⏭️ Photo ${photoObj.id} already ${isCurrentlyUploading ? 'uploading' : 'in queue'}, skipping duplicate`);
            return; // Skip adding to queue
          }
          
          const queueItem = {
            photo: photoObj,
            roomIndex,
            itemIndex,
            retryCount: 0,
            maxRetries: 3
          };
          uploadQueueRef.current.push(queueItem);
          console.log(`[DEBUG] ✅ Queued photo ${index + 1}/${newPhotoObjects.length} for upload:`, {
            photoId: photoObj.id,
            queuePosition: uploadQueueRef.current.length
          });
        });
        
        console.log(`[DEBUG] Upload queue now has ${uploadQueueRef.current.length} items. Starting processing...`);
        
        // Start processing the upload queue
        processUploadQueue();
        
        // Schedule autosave resume after all uploads complete
        // We'll check when the queue is empty in processUploadQueue
      } else {
        console.warn(`[DEBUG] No valid photos to add for R${roomIndex}I${itemIndex}`);
        // Stop processing indicator even if no photos were added
        onProcessingStop?.(roomIndex, itemIndex);
      }
    } catch (error) {
      console.error(`[DEBUG] Error in handlePhotoAddInternal for R${roomIndex}I${itemIndex}:`, error);
      // Stop processing indicator on error
      onProcessingStop?.(roomIndex, itemIndex);
      throw error;
    }
  };

  const handlePhotoUploadEvent = useCallback((roomIndex: number, itemIndex: number, event?: React.ChangeEvent<HTMLInputElement>) => {
    // Increment attempt counter for debugging
    uploadAttemptCounterRef.current += 1;
    const attemptId = uploadAttemptCounterRef.current;
    
    console.log(`[DEBUG] ========== UPLOAD ATTEMPT #${attemptId} STARTED ==========`);
    console.log(`[DEBUG] handlePhotoUploadEvent called for R${roomIndex}I${itemIndex}`);
    console.log(`[DEBUG] Event provided:`, !!event);
    console.log(`[DEBUG] Event target:`, event?.target);
    console.log(`[DEBUG] Files from event:`, event?.target?.files ? Array.from(event.target.files).map(f => ({ name: f.name, size: f.size, type: f.type })) : 'No files');
    
    const files = event?.target?.files;
    if (files && files.length > 0) {
      console.log(`[DEBUG] Processing ${files.length} files for R${roomIndex}I${itemIndex} (attempt #${attemptId})`);
      
      // Start processing indicator now that we have files to process
      onProcessingStart?.(roomIndex, itemIndex);
      
      const dataUrlPromises: Promise<string>[] = Array.from(files).map((file, index) => {
        console.log(`[DEBUG] Creating FileReader promise for file ${index + 1}/${files.length}:`, {
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified
        });
        
        return new Promise((resolve, reject) => {
          // Add file size validation
          if (file.size > 50 * 1024 * 1024) { // 50MB limit
            const errorMsg = `File ${file.name} is too large (${Math.round(file.size / 1024 / 1024)}MB). Maximum size is 50MB.`;
            console.error(`[DEBUG] File size validation failed:`, errorMsg);
            reject(new Error(errorMsg));
            return;
          }

          const reader = new FileReader();
          
          // Add timeout for file reading
          const timeout = setTimeout(() => {
            console.error(`[DEBUG] File reading timeout for ${file.name} after 30 seconds`);
            reader.abort();
            reject(new Error(`File reading timeout for ${file.name}`));
          }, 30000);
          
          // Override onload to clear timeout - fix the context issue
          reader.onload = (e) => {
            clearTimeout(timeout);
            console.log(`[DEBUG] FileReader completed within timeout for ${file.name}`);
            if (e.target?.result) {
              const result = e.target.result.toString();
              console.log(`[DEBUG] FileReader successful for ${file.name}. Result length:`, result.length);
              resolve(result);
            } else {
              const errorMsg = 'FileReader onload error: e.target.result was null or undefined.';
              console.error(`[DEBUG] ${errorMsg}. Event:`, e);
              reject(new Error(errorMsg));
            }
          };
          
          reader.onerror = () => {
            clearTimeout(timeout);
            const errorMsg = `FileReader failed for ${file.name}`;
            console.error(`[DEBUG] ${errorMsg}. Reader error:`, reader.error);
            reject(new Error(errorMsg));
          };
          
          reader.readAsDataURL(file);
        });
      });
      
      Promise.allSettled(dataUrlPromises)
        .then(results => {
          console.log(`[DEBUG] FileReader results for attempt #${attemptId}:`, results.map(r => r.status));
          
          const successfulDataUrls: string[] = [];
          const errors: string[] = [];
          
          results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
              successfulDataUrls.push(result.value);
              console.log(`[DEBUG] File ${index + 1} processed successfully`);
            } else {
              const errorMsg = `File ${index + 1} failed: ${result.reason?.message || 'Unknown error'}`;
              errors.push(errorMsg);
              console.error(`[DEBUG] ${errorMsg}`);
            }
          });
          
          if (successfulDataUrls.length > 0) {
            console.log(`[DEBUG] Calling handlePhotoAddSynchronized with ${successfulDataUrls.length} successful data URLs`);
            handlePhotoAddSynchronized(successfulDataUrls, roomIndex, itemIndex);
          } else {
            console.error(`[DEBUG] No successful data URLs to process for attempt #${attemptId}`);
            // Stop processing indicator if no successful files
            onProcessingStop?.(roomIndex, itemIndex);
          }
          
          if (errors.length > 0) {
            console.error(`[DEBUG] Some files failed to process in attempt #${attemptId}:`, errors);
            // Could show user notification here
          }
        })
        .catch(error => {
          console.error(`[DEBUG] Unexpected error in file processing for attempt #${attemptId}:`, error);
          // Stop processing indicator on unexpected error
          onProcessingStop?.(roomIndex, itemIndex);
        });
    } else {
      console.log(`[DEBUG] No files provided, attempting to trigger file selector or mobile handler for attempt #${attemptId}`);
      
      // Handle browser/mobile photo selection
      if ((window as any).webkit?.messageHandlers?.photoSelection) {
        console.log(`[DEBUG] Using iOS webkit photo selection for attempt #${attemptId}`);
        (window as any).webkit.messageHandlers.photoSelection.postMessage({ multiple: true, roomIndex, itemIndex });
      } else {
        console.log(`[DEBUG] Creating file input element for attempt #${attemptId}`);
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.multiple = true;
        input.onchange = (e) => {
          console.log(`[DEBUG] File input onchange triggered for attempt #${attemptId}`);
          handlePhotoUploadEvent(roomIndex, itemIndex, e as any);
        };
        
        // Remove the cancellation detection since we're not starting processing on button click anymore
        console.log(`[DEBUG] Triggering file input click for attempt #${attemptId}`);
        input.click();
      }
    }
  }, [handlePhotoAddSynchronized, onProcessingStart, onProcessingStop]);

  const handleGallerySelect = useCallback(async (roomIndex: number, itemIndex: number) => {
    try {
      console.log(`[DEBUG] Gallery selection started for R${roomIndex}I${itemIndex}`);
      
      // Try iOS native multiple photo selection first
      if ((window as any).webkit?.messageHandlers?.photoSelection) {
        console.log(`[DEBUG] Using iOS webkit multiple photo selection for R${roomIndex}I${itemIndex}`);
        (window as any).webkit.messageHandlers.photoSelection.postMessage({ 
          multiple: true, 
          roomIndex, 
          itemIndex,
          maxPhotos: 100 // Allow up to 100 photos at once
        });
        return; // Exit early, the iOS handler will call our callback which will start processing
      }
      
      // Fallback to Capacitor single photo selection
      console.log(`[DEBUG] iOS webkit not available, falling back to Capacitor single photo selection`);
      
      const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
      
      // Enhanced camera configuration matching camera capture settings for consistent quality
      const cameraOptions = {
        quality: 90, // Match camera capture quality for consistency
        allowEditing: false, 
        resultType: CameraResultType.DataUrl, 
        source: CameraSource.Photos, 
        correctOrientation: true,
        // Remove size limits to get full quality images that will be properly compressed
        preserveAspectRatio: true
      };

      console.log(`[DEBUG] Attempting Capacitor gallery selection with options:`, cameraOptions);
      
      const image = await Camera.getPhoto(cameraOptions);

      if (image.dataUrl) {
        console.log(`[DEBUG] Capacitor gallery selection successful, starting processing`);
        // Start processing now that we have a photo
        onProcessingStart?.(roomIndex, itemIndex);
        await handlePhotoAddSynchronized([image.dataUrl], roomIndex, itemIndex);
      } else {
        console.warn(`[DEBUG] Capacitor gallery selection returned no data URL`);
        // No processing to stop since we never started it
      }
    } catch (error: any) {
      console.error(`[DEBUG] Error in gallery selection for R${roomIndex}I${itemIndex}:`, error);
      
      // Handle user cancellation
      if (error.message && error.message.includes('cancelled') || error.message.includes('canceled')) {
        console.log(`[DEBUG] Gallery selection was cancelled by user for R${roomIndex}I${itemIndex}`);
      }
      
      // Only stop processing if we actually started it
      // Since we start processing when we get results, cancellation doesn't need to stop processing
    }
  }, [handlePhotoAddSynchronized, onProcessingStart, onProcessingStop]);

  const handleCameraCapture = useCallback(async (roomIndex: number, itemIndex: number) => {
    try {
      console.log(`[DEBUG] Continuous multi-shot camera capture started for R${roomIndex}I${itemIndex}`);
      
      // Try to use a native approach first if available
      if ((window as any).webkit?.messageHandlers?.multiShotCamera) {
        console.log(`[DEBUG] Using iOS webkit multi-shot camera for R${roomIndex}I${itemIndex}`);
        (window as any).webkit.messageHandlers.multiShotCamera.postMessage({ 
          roomIndex, 
          itemIndex,
          maxPhotos: 100 // Allow up to 100 photos in one session
        });
        return; // Exit early, the iOS handler will call our callback
      }
      
      // Import the standard Camera API
      const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');

      // Create a counter overlay for feedback
      const createPhotoCounter = () => {
        const counterDiv = document.createElement('div');
        counterDiv.style.position = 'fixed';
        counterDiv.style.bottom = '20%';
        counterDiv.style.left = '50%';
        counterDiv.style.transform = 'translateX(-50%)';
        counterDiv.style.backgroundColor = 'rgba(0,0,0,0.8)';
        counterDiv.style.color = 'white';
        counterDiv.style.padding = '12px 20px';
        counterDiv.style.borderRadius = '24px';
        counterDiv.style.zIndex = '10000';
        counterDiv.style.fontFamily = 'system-ui, -apple-system, sans-serif';
        counterDiv.style.fontSize = '16px';
        counterDiv.style.fontWeight = 'bold';
        counterDiv.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
        counterDiv.style.backdropFilter = 'blur(5px)';
        // @ts-ignore
        counterDiv.style.webkitBackdropFilter = 'blur(5px)';
        counterDiv.textContent = 'Photos: 0';
        document.body.appendChild(counterDiv);
        return counterDiv;
      };

      // Use multi-shot technique without preview
      const capturedPhotos: string[] = [];
      let photoCount = 0;
      const maxPhotos = 100;
      let continueCapturing = true;
      let counterElement: HTMLDivElement | null = null;
      
      try {
        // First photo - standard approach with no counter yet
        const firstImage = await Camera.getPhoto({
          quality: 85,
        allowEditing: false, 
        resultType: CameraResultType.DataUrl, 
        source: CameraSource.Camera, 
        correctOrientation: true,
          promptLabelHeader: 'Take Multiple Photos',
          promptLabelPhoto: 'Capture',
          promptLabelCancel: 'Done'
        });

        if (firstImage.dataUrl) {
          capturedPhotos.push(firstImage.dataUrl);
          photoCount++;
          
          // Create counter after first successful photo
          counterElement = createPhotoCounter();
          counterElement.textContent = `Photos: ${photoCount}`;
          
          // Now start multi-shot mode - continue taking photos until user cancels
          while (continueCapturing && photoCount < maxPhotos) {
            try {
              const nextImage = await Camera.getPhoto({
                quality: 85,
                allowEditing: false,
                resultType: CameraResultType.DataUrl,
                source: CameraSource.Camera,
                correctOrientation: true,
                promptLabelHeader: `Photos: ${photoCount}`,
                promptLabelPhoto: 'Capture',
                promptLabelCancel: 'Done'
              });

              if (nextImage.dataUrl) {
                capturedPhotos.push(nextImage.dataUrl);
                photoCount++;
                
                if (counterElement) {
                  counterElement.textContent = `Photos: ${photoCount}`;
                  // Visual feedback
                  counterElement.style.backgroundColor = 'rgba(50,205,50,0.8)';
                  setTimeout(() => {
                    if (counterElement) {
                      counterElement.style.backgroundColor = 'rgba(0,0,0,0.8)';
                    }
                  }, 300);
                }
                
                // Vibrate for feedback
                if (navigator.vibrate) {
                  navigator.vibrate(50);
                }
              }
            } catch (error: any) {
              // User likely pressed "Done" or canceled, which is expected behavior to end the session
              if (error.message && (
                  error.message.includes('cancelled') || 
                  error.message.includes('canceled') ||
                  error.message.includes('User cancelled')
              )) {
                console.log(`[DEBUG] User finished multi-shot session with ${photoCount} photos`);
                continueCapturing = false;
      } else {
                // Unexpected error
                console.error(`[DEBUG] Error in multi-shot loop:`, error);
                continueCapturing = false;
              }
            }
          }
      }
    } catch (error: any) {
        // Handle first photo cancellation
        console.log(`[DEBUG] User cancelled initial photo or error occurred:`, error);
      } finally {
        // Remove counter element if it exists
        if (counterElement && counterElement.parentNode) {
          document.body.removeChild(counterElement);
        }
      }
      
      // Process captured photos
      if (capturedPhotos.length > 0) {
        console.log(`[DEBUG] Multi-shot session complete with ${capturedPhotos.length} photos`);
        onProcessingStart?.(roomIndex, itemIndex);
        await handlePhotoAddSynchronized(capturedPhotos, roomIndex, itemIndex);
      } else {
        console.log(`[DEBUG] No photos captured in multi-shot session`);
      }
    } catch (error: any) {
      console.error(`[DEBUG] Error in camera capture for R${roomIndex}I${itemIndex}:`, error);
    }
  }, [handlePhotoAddSynchronized, onProcessingStart, onProcessingStop]);

  // Cleanup function for timeouts and refs
  useEffect(() => {
    return () => {
      // Clear all retry timeouts on unmount
      retryTimeoutsRef.current.forEach((timeout) => {
        clearTimeout(timeout);
      });
      retryTimeoutsRef.current.clear();
      
      // Clear upload queue
      uploadQueueRef.current = [];
      ongoingUploadsRef.current.clear();
      operationQueueRef.current.clear();
    };
  }, []);

  useEffect(() => {
    // Handler for photos coming from iOS native multiple selection
    (window as any).handleMultiplePhotosFromiOS = (
      photoDataArray: string[],
      roomIndex: number, 
      itemIndex: number
    ) => {
      console.log(`[DEBUG] ========== iOS MULTIPLE PHOTOS RECEIVED ==========`);
      console.log(`[DEBUG] Received ${photoDataArray?.length || 0} photos from iOS for R${roomIndex}I${itemIndex}`);
      console.log(`[DEBUG] Photo data array type:`, typeof photoDataArray);
      console.log(`[DEBUG] Photo data array length:`, photoDataArray?.length);
      console.log(`[DEBUG] Room index:`, roomIndex, typeof roomIndex);
      console.log(`[DEBUG] Item index:`, itemIndex, typeof itemIndex);
      
      // Validate inputs
      if (!Array.isArray(photoDataArray)) {
        console.error(`[DEBUG] Invalid photoDataArray - not an array:`, photoDataArray);
        return;
      }
      
      if (photoDataArray.length === 0) {
        console.warn(`[DEBUG] Empty photoDataArray received`);
        return;
      }
      
      if (typeof roomIndex !== 'number' || typeof itemIndex !== 'number') {
        console.error(`[DEBUG] Invalid room/item indices:`, { roomIndex, itemIndex });
        return;
      }
      
      // Validate each photo data URL
      const validPhotos = photoDataArray.filter((photo, index) => {
        if (typeof photo !== 'string') {
          console.error(`[DEBUG] Photo ${index + 1} is not a string:`, typeof photo);
          return false;
        }
        if (!photo.startsWith('data:image/')) {
          console.error(`[DEBUG] Photo ${index + 1} is not a valid data URL:`, photo.substring(0, 50) + '...');
          return false;
        }
        console.log(`[DEBUG] Photo ${index + 1} validated - length: ${photo.length}`);
        return true;
      });
      
      if (validPhotos.length === 0) {
        console.error(`[DEBUG] No valid photos found in the array`);
        return;
      }
      
      if (validPhotos.length !== photoDataArray.length) {
        console.warn(`[DEBUG] Some photos were invalid - processing ${validPhotos.length} out of ${photoDataArray.length}`);
      }
      
      console.log(`[DEBUG] Starting processing for ${validPhotos.length} valid photos from iOS`);
      // Start processing now that we have photos from iOS
      onProcessingStart?.(roomIndex, itemIndex);
      
      console.log(`[DEBUG] Processing ${validPhotos.length} valid photos through handlePhotoAddSynchronized`);
      handlePhotoAddSynchronized(validPhotos, roomIndex, itemIndex);
    };
    
    // Handler for photos coming from iOS multi-shot camera
    (window as any).handleMultiShotCameraPhotos = (
      photoDataArray: string[],
      roomIndex: number, 
      itemIndex: number
    ) => {
      console.log(`[DEBUG] ========== iOS MULTI-SHOT CAMERA PHOTOS RECEIVED ==========`);
      console.log(`[DEBUG] Received ${photoDataArray?.length || 0} photos from multi-shot camera for R${roomIndex}I${itemIndex}`);

      // Reuse the same validation and processing logic as handleMultiplePhotosFromiOS
      if (!Array.isArray(photoDataArray)) {
        console.error(`[DEBUG] Invalid photoDataArray from multi-shot camera - not an array`);
        return;
      }
      
      if (photoDataArray.length === 0) {
        console.warn(`[DEBUG] Empty photoDataArray from multi-shot camera`);
        return;
      }
      
      if (typeof roomIndex !== 'number' || typeof itemIndex !== 'number') {
        console.error(`[DEBUG] Invalid room/item indices from multi-shot camera:`, { roomIndex, itemIndex });
        return;
      }
      
      // Validate each photo data URL
      const validPhotos = photoDataArray.filter((photo, index) => {
        if (typeof photo !== 'string') {
          console.error(`[DEBUG] Multi-shot photo ${index + 1} is not a string:`, typeof photo);
          return false;
        }
        if (!photo.startsWith('data:image/')) {
          console.error(`[DEBUG] Multi-shot photo ${index + 1} is not a valid data URL`);
          return false;
        }
        return true;
      });
      
      if (validPhotos.length === 0) {
        console.error(`[DEBUG] No valid photos found in the multi-shot array`);
        return;
      }
      
      console.log(`[DEBUG] Starting processing for ${validPhotos.length} multi-shot photos`);
      onProcessingStart?.(roomIndex, itemIndex);
      handlePhotoAddSynchronized(validPhotos, roomIndex, itemIndex);
    };
    
    console.log(`[DEBUG] iOS multiple photo and multi-shot camera handlers registered on window`);
    
    return () => { 
      console.log(`[DEBUG] Cleaning up iOS photo handlers`);
      delete (window as any).handleMultiplePhotosFromiOS; 
      delete (window as any).handleMultiShotCameraPhotos;
    };
  }, [handlePhotoAddSynchronized, onProcessingStart]);

  return { 
    handleRoomPhotoUpload: handlePhotoUploadEvent, 
    handleGallerySelect,
    handleCameraCapture,
    // Direct data URL upload for camera captures (RetrofitSurvey compatibility)
    handlePhotoUploadFromDataUrls: handlePhotoAddSynchronized
  };
}; 